import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    GridItem,
    Input,
    Select,
    SimpleGrid,
    useColorModeValue
} from "@chakra-ui/react";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {checkErrors, hasError} from "../../../../utils/forms-utils";
import {useTranslation} from "react-i18next";
import {ROUTES} from "../../../../constants/routes-path";
import {ufs} from "../../../../utils";
import DateTime from "react-datetime";
import {DATE_FORMAT, LOCALE_PT_BR} from "../../../../constants/date-time";
import moment from "moment/moment";
import {FaMapMarkerAlt} from "react-icons/fa";

function BoatLocationForm({onSubmit, values}) {

    const navigate = useNavigate();

    const ids = useMemo(() => ({
        local: "local",
        bairro: "bairro",
        uf: "uf",
        cidade: "cidade",
        dataHoraAcidente: "dataHoraAcidente",
        pontoReferencia: "pontoReferencia",
        latitude: "latitude",
        longitude: "longitude",
    }), []);

    const initFormData = (data) => ({
        [ids.local]: {
            value: data?.local || "",
            validations: [{required: true}],
        },
        [ids.bairro]: {
            value: data?.bairro || "",
            validations: [{required: true}],
        },
        [ids.uf]: {
            value: data?.uf || "",
            validations: [{required: true}],
        },
        [ids.cidade]: {
            value: data?.cidade || "",
            validations: [{required: true}],
        },
        [ids.dataHoraAcidente]: {
            value: data?.dataHoraAcidente || "",
            validations: [{required: true}],
        },
        [ids.pontoReferencia]: {
            value: data?.pontoReferencia || "",
            validations: [],
        },
        [ids.latitude]: {
            value: data?.geoLocalizacao?.latitude || "",
            validations: [],
        },
        [ids.longitude]: {
            value: data?.geoLocalizacao?.longitude || "",
            validations: [],
        },
    });

    const {t} = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const [formData, setFormData] = useState(initFormData(values));
    const [inputError, setInputError] = useState({});
    const [isGettingLocation, setIsGettingLocation] = useState(false);

    const isFormInvalid = formData && inputError ? hasError({formData, inputError}) : true;

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = {...formData, [id]: {...formData[id], value}};
        setFormData(newFormData);
        setInputError(checkErrors({formData: newFormData, inputError, name: id}));
    };

    const handleDateChange = (value) => {
        const dateValue = moment(value).isValid()
            ? moment(value).toISOString()
            : "";

        const newFormData = {
            ...formData,
            [ids.dataHoraAcidente]: {...formData[ids.dataHoraAcidente], value: dateValue}
        };
        setFormData(newFormData);
    };

    const handleGpsClick = () => {
        if (!navigator.geolocation) return;
        setIsGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const {latitude, longitude} = position.coords;
                setFormData((prev) => ({
                    ...prev,
                    [ids.latitude]: {...prev[ids.latitude], value: String(latitude)},
                    [ids.longitude]: {...prev[ids.longitude], value: String(longitude)},
                }));
                setIsGettingLocation(false);
            },
            () => {
                setIsGettingLocation(false);
            }
        );
    };

    const handleOnSubmit = () => {
        const newValues = {
            local: formData.local?.value || '',
            bairro: formData.bairro?.value || '',
            uf: formData.uf?.value || '',
            cidade: formData.cidade?.value || '',
            dataHoraAcidente: formData.dataHoraAcidente?.value || '',
            pontoReferencia: formData.pontoReferencia?.value || '',
            geoLocalizacao: {
                latitude: formData.latitude?.value || '',
                longitude: formData.longitude?.value || '',
            },
        };

        onSubmit(newValues);
    };

    return (
        <>
            <SimpleGrid spacing={2} columns={{sm: 1, md: 2}}>
                <FormControl isRequired isInvalid={inputError[ids.local]}>
                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                        {t("boat.new.location")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        variant="main"
                        id={ids.local}
                        value={formData[ids.local].value}
                        onChange={handleInputChange}
                        placeholder={t('boat.new.location-placeholder')}
                        _placeholder={{color: placeholderColor}}
                    />
                    <FormErrorMessage>
                        {t(inputError[ids.local])}
                    </FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={inputError[ids.bairro]}>
                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                        {t("boat.new.region")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        variant="main"
                        id={ids.bairro}
                        value={formData[ids.bairro].value}
                        onChange={handleInputChange}
                        placeholder={t('boat.new.region')}
                        _placeholder={{color: placeholderColor}}
                    />
                    <FormErrorMessage>
                        {t(inputError[ids.bairro])}
                    </FormErrorMessage>
                </FormControl>

                <SimpleGrid spacing={2} columns={{sm: 1, md: 3}}>
                    <FormControl isRequired isInvalid={inputError[ids.uf]} w>
                        <FormLabel fontSize="xs" fontWeight="bold">
                            {t(`boat.new.state`)}
                        </FormLabel>
                        <Select
                            fontSize="sm"
                            variant="main"
                            id={ids.uf}
                            value={formData[ids.uf].value?.toUpperCase()}
                            onChange={handleInputChange}
                            placeholder={t(`boat.new.state`)}
                        >
                            {ufs.map((uf) => (
                                <option key={uf.value} value={uf.value}>
                                    {uf.value} - {uf.label}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>
                            {t(inputError[ids.uf])}
                        </FormErrorMessage>
                    </FormControl>
                    <GridItem colSpan={2}>
                        <FormControl isRequired isInvalid={inputError[ids.cidade]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("boat.new.city")}
                            </FormLabel>
                            <Input
                                fontSize="sm"
                                variant="main"
                                id={ids.cidade}
                                value={formData[ids.cidade].value}
                                onChange={handleInputChange}
                                placeholder={t('boat.new.city')}
                                _placeholder={{color: placeholderColor}}
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.cidade])}
                            </FormErrorMessage>
                        </FormControl>
                    </GridItem>
                </SimpleGrid>

                <FormControl isRequired isInvalid={inputError[ids.dataHoraAcidente]}>
                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                        {t("boat.new.accident-date-time")}
                    </FormLabel>
                    <DateTime
                        dateFormat={DATE_FORMAT}
                        timeFormat={true}
                        locale={LOCALE_PT_BR}
                        initialValue={
                            formData[ids.dataHoraAcidente].value
                                ? moment(formData[ids.dataHoraAcidente].value)
                                : ""
                        }
                        renderInput={(props) => (
                            <Input
                                {...props}
                                variant="main"
                                fontSize="sm"
                                placeholder={t("boat.new.accident-date-time")}
                            />
                        )}
                        onChange={handleDateChange}
                    />
                    <FormErrorMessage>
                        {t(inputError[ids.dataHoraAcidente])}
                    </FormErrorMessage>
                </FormControl>

                <FormControl>
                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                        {t("boat.new.reference")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        variant="main"
                        id={ids.pontoReferencia}
                        value={formData[ids.pontoReferencia].value}
                        onChange={handleInputChange}
                        placeholder={t('boat.new.reference')}
                        _placeholder={{color: placeholderColor}}
                    />
                </FormControl>

                <GridItem colSpan={{sm: 1, md: 2}}>
                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                        {t("boat.new.geo-location")}
                    </FormLabel>
                    <Flex gap={2} align="flex-end">
                        <Button
                            variant="dark"
                            fontSize="sm"
                            flexShrink={0}
                            onClick={handleGpsClick}
                            isLoading={isGettingLocation}
                            loadingText={t("boat.new.getting-location")}
                            leftIcon={<FaMapMarkerAlt />}
                        >
                            {t("boat.new.get-gps")}
                        </Button>
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("boat.new.latitude")}
                            </FormLabel>
                            <Input
                                fontSize="sm"
                                variant="main"
                                id={ids.latitude}
                                value={formData[ids.latitude].value}
                                onChange={handleInputChange}
                                placeholder={t("boat.new.latitude")}
                                _placeholder={{color: placeholderColor}}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("boat.new.longitude")}
                            </FormLabel>
                            <Input
                                fontSize="sm"
                                variant="main"
                                id={ids.longitude}
                                value={formData[ids.longitude].value}
                                onChange={handleInputChange}
                                placeholder={t("boat.new.longitude")}
                                _placeholder={{color: placeholderColor}}
                            />
                        </FormControl>
                    </Flex>
                </GridItem>

            </SimpleGrid>

            <Flex w="100%" justify="space-between" mt="20px">
                <Button
                    variant="light"
                    fontSize="sm"
                    w="125px"
                    onClick={() => navigate(ROUTES.boatsPath)}
                >
                    {t("system.actions.cancel")}
                </Button>
                <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    onClick={handleOnSubmit}
                    disabled={isFormInvalid}
                >
                    {t('system.actions.next')}
                </Button>
            </Flex>
        </>
    )
}

export default BoatLocationForm;